import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../images/logo/logo.png';
import Loader from '../../common/Loader';
import { tokenStorage, useUserStore } from '../../stores/userStore';
import { showError } from '../../utils/notifications';
import api from '../../config/axios';
import { AUTH } from '../../config/apiPaths';
import PageTitle from '../../components/UiElements/PageTitle';
import { prepareTenantHandoff } from '../../services/tenantHandoff';
import {
  configuredTenantOrigin,
  navigateExternal,
  parseFromParam,
  planPostLogin,
  tenantDirectLoginUrl,
} from '../../utils/unifiedSignIn';

interface Notice {
  message: string;
  /** The tenant app's own sign-in, offered when the automatic hand-off cannot happen. */
  fallbackUrl?: string;
}

const FALLBACK_REDIRECT_MS = 5000;

const SignIn: React.FC = () => {
  const { login, loginDemo, isLoading, error } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenant = parseFromParam(location.search) === 'tenant';
  const tenantOrigin = configuredTenantOrigin();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  // If the automatic hand-off to the tenant app fails, send the tenant to that app's own
  // sign-in after a short pause (the message and link stay visible meanwhile).
  useEffect(() => {
    if (!notice?.fallbackUrl) return;
    const url = notice.fallbackUrl;
    const t = setTimeout(() => navigateExternal(url), FALLBACK_REDIRECT_MS);
    return () => clearTimeout(t);
  }, [notice]);

  // Show error notification when login fails
  useEffect(() => {
    if (error && !isLoading) {
      showError(error);
      // Clear the error after a delay to ensure notification is shown
      setTimeout(() => {
        useUserStore.getState().clearError();
      }, 100);
    }
  }, [error, isLoading]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // useColorMode (the hook the rest of the app uses) stores this
    // JSON-encoded (i.e. '"dark"', with quotes) via useLocalStorage — a
    // plain string comparison here never matched that, so this page could
    // silently disagree with the theme applied everywhere else.
    const savedTheme = localStorage.getItem('color-theme');
    const isDark = savedTheme === '"dark"' || savedTheme === 'dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Drop any session held in THIS app (used when the user belongs in the other app).
  const discardLocalSession = async () => {
    tokenStorage.setToken(null);
    await useUserStore.getState().logout();
    if (window.tokenRefreshTimeout) clearTimeout(window.tokenRefreshTimeout);
  };

  // Landlord-side roles stay; tenants are handed to the tenant app. Never throws.
  const routeByRole = async (token: string | null, role: string | undefined, storedLocally: boolean) => {
    const plan = token
      ? planPostLogin({
          token,
          userRole: role,
          tenantAppUrl: import.meta.env.VITE_TENANT_APP_URL,
          currentOrigin: window.location.origin,
          currentHostname: window.location.hostname,
        })
      : ({ action: 'stay' } as const);

    if (plan.action === 'stay') {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (plan.action === 'blocked') {
      if (storedLocally) await discardLocalSession();
      setNotice({ message: plan.message, fallbackUrl: tenantOrigin ? tenantDirectLoginUrl(tenantOrigin) : undefined });
      return;
    }
    setBusy(true);
    try {
      const result = await prepareTenantHandoff(token as string, plan.origin);
      if (storedLocally) await discardLocalSession();
      if (result.ok) {
        navigateExternal(result.url);
        return; // keep the loader up while the browser navigates
      }
      setNotice({ message: result.message, fallbackUrl: result.fallbackUrl });
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      showError('Please enter both email and password');
      return;
    }
    
    try {
      // Clear any previous errors
      useUserStore.getState().clearError();
      
      // Call the login function and wait for it to complete
      const userData = await login(email, password);
      
      // If we get here, login was successful and we have a user
      if (userData) {
        await routeByRole(useUserStore.getState().token, userData.role, true);
      }
    } catch (err) {
      // Error is handled by the error effect above
      console.error('Login error in handleSubmit:', err);
    }
  }

  const handleDemoLogin = async () => {
    try {
      useUserStore.getState().clearError();
      const userData = await loginDemo();
      if (userData) {
        await routeByRole(useUserStore.getState().token, userData.role, true);
      }
    } catch (err) {
      console.error('Demo login error:', err);
    }
  }

  // Tenant demo: fetch the seeded tenant session WITHOUT storing it in this app, then hand
  // it to the tenant app. If the server ignores ?role=tenant and returns a landlord token,
  // nothing is stored or handed over.
  const handleTenantDemoLogin = async () => {
    setNotice(null);
    setBusy(true);
    try {
      const response = await api.post(`${AUTH.LOGIN_DEMO}?role=tenant`, {});
      const token: string | undefined = response?.data?.token;
      const role: string | undefined = response?.data?.user?.role;
      const plan = token
        ? planPostLogin({
            token,
            userRole: role,
            tenantAppUrl: import.meta.env.VITE_TENANT_APP_URL,
            currentOrigin: window.location.origin,
            currentHostname: window.location.hostname,
          })
        : null;
      if (!token || !plan || plan.action === 'stay') {
        setNotice({ message: 'The tenant demo is not available on this server.' });
        return;
      }
      if (plan.action === 'blocked') {
        setNotice({ message: plan.message });
        return;
      }
      const result = await prepareTenantHandoff(token, plan.origin);
      if (result.ok) {
        navigateExternal(result.url);
        return;
      }
      setNotice({ message: result.message, fallbackUrl: result.fallbackUrl });
    } catch (err) {
      console.error('Tenant demo login error:', err);
      setNotice({ message: 'Demo login failed. The demo may not be enabled on this server.' });
    } finally {
      setBusy(false);
    }
  };

  // navigate to dashboard if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/dashboard');
    }
  }, [navigate]);

  if (isLoading || busy) {
    return <Loader />;
  }

  return (
    <div className="flex min-h-screen w-full">
      <PageTitle title="Sign in" />
      {/* Left side with logo and background */}
      <div className="hidden xl:flex xl:w-1/2">
        <div className="relative w-full overflow-hidden bg-gradient-to-br from-green-600 to-green-800">
          <div className="flex h-full items-center justify-center p-10">
            <div className="text-center">
              <img 
                src={Logo} 
                alt="Re:Loam" 
                className="mx-auto mb-8 h-32 w-auto opacity-90" 
              />
              <h1 className="mb-4 text-4xl font-bold text-white">Welcome to Re:Loam</h1>
              <p className="text-xl text-green-100">Your land management solution</p>
              <p className="text-xl text-green-100">Need an account? Email reloam@ainaeco.uk.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side with login form */}
      <div className="flex w-full items-center justify-center bg-white dark:bg-boxdark-2 xl:w-1/2">
        <div className="w-full max-w-md p-4 sm:p-12.5">
          <div className="mb-6 flex justify-center">
                <img 
                  src={Logo} 
                  alt="Re:L Logo" 
                  className="h-16 w-auto" 
                />
              </div>
              <h2 className="mb-4 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                Sign In
              </h2>
              <p className="mb-9 text-gray-600 dark:text-gray-300">Sign in to continue to your account</p>

              {fromTenant && !notice && (
                <div className="mb-6 rounded-lg border border-stroke p-4 text-sm text-gray-700 dark:border-form-strokedark dark:text-gray-300">
                  <p>Tenant? Sign in with your tenant email and password and we will take you to the tenant portal.</p>
                  {tenantOrigin && (
                    <p className="mt-2">
                      <a href={tenantDirectLoginUrl(tenantOrigin)} className="text-primary hover:underline">
                        Having trouble? Use the tenant sign-in
                      </a>
                    </p>
                  )}
                </div>
              )}

              {notice && (
                <div role="alert" className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
                  <p>{notice.message}</p>
                  {notice.fallbackUrl && (
                    <p className="mt-2">
                      <a href={notice.fallbackUrl} className="font-medium underline">
                        Use the tenant sign-in
                      </a>
                    </p>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      data-cy="email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      autoComplete="email"
                      className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary dark:placeholder:text-gray-400"
                    />

                    <span className="absolute right-4 top-4">
                      <svg
                        className="fill-current"
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.5">
                          <path
                            d="M19.2516 3.30005H2.75156C1.58281 3.30005 0.585938 4.26255 0.585938 5.46567V16.6032C0.585938 17.7719 1.54844 18.7688 2.75156 18.7688H19.2516C20.4203 18.7688 21.4172 17.8063 21.4172 16.6032V5.4313C21.4172 4.26255 20.4203 3.30005 19.2516 3.30005ZM19.2516 4.84692C19.2859 4.84692 19.3203 4.84692 19.3547 4.84692L11.0016 10.2094L2.64844 4.84692C2.68281 4.84692 2.71719 4.84692 2.75156 4.84692H19.2516ZM19.2516 17.1532H2.75156C2.40781 17.1532 2.13281 16.8782 2.13281 16.5344V6.35942L10.1766 11.5157C10.4172 11.6875 10.6922 11.7563 10.9672 11.7563C11.2422 11.7563 11.5172 11.6875 11.7578 11.5157L19.8016 6.35942V16.5688C19.8703 16.9125 19.5953 17.1532 19.2516 17.1532Z"
                            fill=""
                          />
                        </g>
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      data-cy="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary dark:placeholder:text-gray-400"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
             
                <div className="mb-4 flex items-center justify-between">
                  <Link to="/auth/forgot-password" className="inline-flex min-h-[40px] items-center text-sm text-primary hover:underline">
                    Forgot Password?
                  </Link>
                </div>

                <div className="mb-5">
                  <input
                    type="submit"
                    value="Sign In"
                    data-cy="login-button"
                    className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-opacity-90"
                  />
                </div>

                {import.meta.env.VITE_DEMO_LOGIN_ENABLED === 'true' && (
                  <>
                    <div className="mb-5">
                      <button
                        type="button"
                        onClick={handleDemoLogin}
                        data-cy="demo-login-button"
                        className="w-full cursor-pointer rounded-lg border border-primary bg-transparent p-4 text-primary transition hover:bg-primary hover:bg-opacity-10"
                      >
                        Explore as landlord
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="button"
                        onClick={handleTenantDemoLogin}
                        data-cy="demo-tenant-login-button"
                        className="w-full cursor-pointer rounded-lg border border-primary bg-transparent p-4 text-primary transition hover:bg-primary hover:bg-opacity-10"
                      >
                        Explore as tenant
                      </button>
                    </div>
                  </>
                )}

                <div className="mt-6 text-center">
                  <p>
                    Don't have an account?{' '}
                    <a href="mailto:reloam@ainaeco.uk?subject=Re:Loam%20access%20request" className="inline-flex min-h-[40px] items-center text-primary">
                      Request access
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
  );
};
export default SignIn;
